<template>
    <div class="px-2 md:px-4 pt-2 md:pt-14 pb-4 h-full">
        <template v-if="!showEnding">
            <div v-if="mode != 'edit'"
                class="mb-4 p-4 flex items-center bg-gradient-to-br from-primary/5 to-purple-100 dark:from-neutral-700/50 dark:to-neutral-800/50 rounded-lg border border-gray-200 dark:border-neutral-700 shadow-sm gap-4 flex-col sm:flex-row md:flex-col lg:flex-row">

                <div class="flex gap-4 items-center">
                    <!-- Icon -->
                    <div class="flex items-center justify-center w-12 h-12 bg-primary_opc_less rounded-full flex-shrink-0">
                        <svg-vue icon="marker" class="w-6 h-6 text-primary"></svg-vue>
                    </div>

                    <!-- Content -->
                    <div class="flex-1">
                        <h3 class="text-lg font-semibold text-gray-800 dark:text-white">
                            {{ $t('Add a new location') }}
                        </h3>
                        <p class="text-sm text-gray-600 dark:text-neutral-300 mt-1">
                            {{ $t('Submit your location from your dashboard. Our team will review it before it goes live on the platform.') }}
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="!canEditLocation"
                class="p-4 mb-4 flex items-start bg-gray-50 rounded-lg border border-gray-200 dark:border-neutral-700 dark:bg-neutral-800 shadow-sm gap-4 flex-col sm:flex-row md:flex-col lg:flex-row">
                <div class="flex gap-4 items-center">
                    <div class="flex-1">
                        <span class="text-base font-semibold text-gray-800 dark:text-white capitalize">
                            {{ $t('Location cannot be editted') }}
                        </span>
                        <div class="text-xs text-gray-600 dark:text-neutral-300 mt-1">
                            {{ $t('location_cannot_edit') }}
                        </div>
                    </div>
                </div>
            </div>

            <form ref="location">
                <app-tabs :tabs-count="5" ref="locationtabs" v-model="step" @step-change="onStepChange">
                    <template #title-0>
                        {{ $t('General') }}
                    </template>

                    <template #content-0>
                        <div class="grid gap-6 my-4 md:grid-cols-1 h-fit">
                            <!-- Location Name -->
                            <div>
                                <label for="Title" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                    {{ $t("Location Name") }}
                                </label>
                                <input type="text" id="title" name="title" v-model="name" :disabled="!canEditLocation"
                                    :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white"
                                    :placeholder="$t('Enter location name') + '...'" required />
                            </div>

                            <div class="flex flex-col md:flex-row gap-6 w-full">
                                <div class="w-full">
                                    <label for="Title" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                        {{ $t("Location Type") }}
                                    </label>
                                    <select id="type" name="type" v-model="type" :disabled="!canEditLocation"
                                        :class="[!canEditLocation ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-neutral-400 dark:text-white">
                                        <option value="">{{ $t('Select type') }}</option>
                                        <option value="food_and_drinks">{{ $t('Food and Drinks') }}</option>
                                        <option value="amusement">{{ $t('Amusement') }}</option>
                                        <option value="must_see">{{ $t('Must see') }}</option>
                                        <option value="park">{{ $t('Park') }}</option>
                                        <option value="health">{{ $t('Health') }}</option>
                                        <option value="services">{{ $t('Services') }}</option>
                                        <option value="shopping">{{ $t('Shopping') }}</option>
                                        <option value="activity">{{ $t('Activity') }}</option>
                                        <option value="transportation">{{ $t('Transportation') }}</option>
                                        <option value="parking">{{ $t('Parking') }}</option>
                                    </select>
                                </div>

                                <div class="w-full">
                                    <label class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">{{ $t('Price indication') }}</label>
                                    <div class="flex flex-wrap gap-2 w-full justify-between">
                                        <label
                                            v-for="option in priceOptions"
                                            :key="option.value"
                                            :class="[
                                                'cursor-pointer flex items-center px-4 h-[42px] rounded-lg border text-sm font-medium transition',
                                                priceIndication === option.value
                                                    ? 'bg-primary text-white border-primary'
                                                    : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100 dark:bg-neutral-700 dark:text-neutral-200 dark:border-neutral-600 dark:hover:bg-neutral-600',
                                                !canEditLocation ? 'opacity-50 cursor-not-allowed' : ''
                                            ]"
                                        >
                                            <input
                                                type="radio"
                                                name="priceIndication"
                                                :value="option.value"
                                                v-model="priceIndication"
                                                :disabled="!canEditLocation"
                                                class="sr-only"
                                            />
                                            {{ option.label }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>


                    <template #title-1>
                        {{ $t('Description') }}
                    </template>
                    <template #content-1>
                        <div class="grid gap-6 mt-4 md:grid-cols-1 h-fit">
                            <div class="pb-4 h-fit">
                                <label for="description" class="block text-sm font-medium text-gray-900 dark:text-white">
                                    {{ $t("Location Description") }}
                                </label>
                                <div class="text-xs dark:text-neutral-200 mb-2">
                                    {{ $t('in english') }}
                                </div>

                                <small_text_editor textareaName="description" :initialContent="initialDescription"
                                    @changedValue="changeDescription"></small_text_editor>
                            </div>
                        </div>
                    </template>

                    <template #title-2>
                        {{ $t('Images') }}
                    </template>

                    <template #content-2>
                        <div class="grid gap-6 mt-4 md:grid-cols-2 h-fit">
                            <div>

                                <label for="image_upload" class="block text-sm font-medium text-gray-900 dark:text-white">
                                    {{ $t("Location Image") }}
                                </label>
                                <div class="text-xs dark:text-neutral-200 mb-2">
                                    {{ $t('Upload an image in PNG, JPEG, JPG, or WEBP format (max. 2MB)') }}
                                </div>

                                <img v-if="image_preview || image_upload" :src="image_preview && image_preview.length ? image_preview : image_upload" class="w-full rounded-md mb-4 h-64 object-cover"
                                    alt="">

                                <input type="file" ref="image_upload" id="image_upload" @change="handleImageUpload"
                                    accept="image/png, image/jpeg, image/jpg, image/webp" name="image_upload"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white" />
                            </div>


                            <div>
                                <label for="logo_upload" class="block text-sm font-medium text-gray-900 dark:text-white">
                                    {{ $t("Location Logo") }}
                                </label>
                                <div class="text-xs dark:text-neutral-200 mb-2">
                                    {{ $t('Upload a logo in PNG, JPEG, JPG, or WEBP format (max. 2MB)') }}
                                </div>

                                <img v-if="logo_preview || logo_upload" :src="logo_preview && logo_preview.length ? logo_preview : logo_upload" class="w-64 h-64 object-contain mb-4 mx-auto rounded-full" alt="">

                                <input type="file" ref="logo_upload" id="logo_upload" @change="handleLogoUpload"
                                    accept="image/png, image/jpeg, image/jpg, image/webp" name="logo_upload"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white" />
                            </div>
                        </div>
                    </template>

                    <template #title-3>
                        {{ $t('Location') }}
                    </template>

                    <template #content-3>
                        <div class="grid gap-6 mt-4 md:grid-cols-2 h-fit">
                            <div class="col-span-2 h-fit">
                                <mapsearch :canEditLocation="mode != 'edit'" @changedValue="changedLocation" :location="location"></mapsearch>
                            </div>
                        </div>
                    </template>

                    <template #title-4>
                        {{ $t('Opening Hours') }}
                    </template>

                    <template #content-4>
                        <div class="grid gap-6 my-4 md:grid-cols-1 h-fit">
                            <OpeningHours
                                :disabled="!canEditLocation"
                                :initialOpeningHours="initialOpeningHours"
                                :initialSpecialOpeningHours="initialSpecialOpeningHours"
                                :initHasOpeningHours="hasOpeningHours"
                                @update:openingHours="updateOpening"
                                @update:specialOpeningHours="updateSpecial"
                                @update:hasOpeningHours="updateHasOpeningHours"
                            />
                        </div>
                    </template>
                </app-tabs>
                <div class="w-full flex justify-between mt-2 md:mt-4">
                    <div v-if="step > 0" @click="back()"
                        class="text-white bg-gray-400 dark:bg-neutral-600 hover:bg-gray-600 dark:hover:bg-neutral-500 cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                        {{ $t('Back') }}
                    </div>
                    <div v-else class="w-fit"></div>

                    <div v-if="step == 0" @click="next()"
                        class="text-white bg-primary hover:bg-primary-dark cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                        {{ $t('Next') }}
                    </div>
                    <div v-else-if="step < 4" @click="next()"
                        class="text-white bg-primary hover:bg-primary-dark cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                        {{ $t('Next') }}
                    </div>
                    <div v-else-if="step == 4 && canEditLocation" @click="save()"
                        class="text-white bg-primary hover:bg-primary-dark cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                        {{ $t('Save and confirm') }}
                    </div>
                    <div v-else class="w-fit"></div>
                </div>
            </form>
        </template>
        <template v-else>
            <div class="mb-4 p-4 flex items-center bg-gradient-to-br from-primary/5 to-purple-100 dark:from-neutral-700/50 dark:to-neutral-800/50 rounded-lg border border-gray-200 dark:border-neutral-700 shadow-sm gap-4 flex-col sm:flex-row md:flex-col lg:flex-row">
                <div class="flex gap-4 items-center">
                    <!-- Icon -->
                    <div class="flex items-center justify-center w-12 h-12 bg-primary_opc_less rounded-full flex-shrink-0">
                        <svg-vue icon="thumbup" class="w-6 h-6 text-primary"></svg-vue>
                    </div>

                    <!-- Content -->
                    <div class="flex-1">
                        <h3 class="text-lg font-semibold text-gray-800 dark:text-white">
                            {{ $t('Thank you') }}!
                        </h3>
                        <p v-if="mode != 'edit'" class="text-sm text-gray-600 dark:text-neutral-300 mt-1">
                            {{ $t('We will review your location and publish it once it meets our terms and conditions') }}.
                        </p>
                        <p v-else class="text-sm text-gray-600 dark:text-neutral-300 mt-1">
                            {{ $t('The location has been editted') }}!
                        </p>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
    
<script>
import AppTabs from "../../../components/elements/appTabs.vue";
import AppAccordion from "../../../components/elements/appAccordion.vue";



export default {
    components: { AppAccordion, AppTabs },
    props: {
        client: { type: Object, default: () => ({}) },
        mode: {
            type: String,
            default: 'add'
        },
    },
    data() {
        return {
            step: 0,
            locationUuid: null,
            resourceType: "",
            location: null,
            showEnding: false,
            canEditLocation: true,

            name: "",
            street: "",
            maps_url: "",
            lat: "",
            long: "",
            city: "",
            postcode: "",
            country: "",
            initialDescription: "",
            description: "",
            default_name: "",
            image_upload: "",
            image_preview: "",
            logo_upload: "",
            logo_preview: "",
            slug: "",
            type: "",

            initialOpeningHours: [],
            openingHours: [],
            initialSpecialOpeningHours: [],
            specialOpeningHours: [],
            hasOpeningHours: true,

            priceIndication: 0,
            priceOptions: [
                { value: 0, label: this.$t('Free') },
                { value: 1, label: '€' },
                { value: 2, label: '€€' },
                { value: 3, label: '€€€' },
                { value: 4, label: '€€€€' },
            ]

        }
    },
    computed: {
        minDate() {
            return new Date().toISOString().split("T")[0];
        },
    },
    methods: {
        back() {
            if (this.step > 0) this.step--;
        },
        next() {
            if (this.step < 4) this.step++;
        },
        save() {
            this.saveLocation();
        },
        async fetchPermission() {
            try {
                this.loading = true;

                const res = await axios.get("/api/dashboard/locations", {
                    params: {
                        uuid: this.locationUuid || "",
                    }
                });

                this.canEditLocation = res.data;
            } catch (error) {
                this.canEditLocation = false;
                if (error.response?.status === 401) {
                    console.warn('User unauthenticated. Attempting refresh...');
                    window.location.reload();
                } else {
                    console.error('Error fetching locations:', error);
                }
            } finally {
                this.loading = false;
            }
        },
        updateSpecial(event) {
            this.specialOpeningHours = event;
            this.$refs.locationtabs.recalculateHeight()
        },
        updateOpening(event) {
            this.openingHours = event;
            this.$refs.locationtabs.recalculateHeight()
        },
        updateHasOpeningHours(event) {
            this.hasOpeningHours = event;
            this.$refs.locationtabs.recalculateHeight()
        },
        // // Fetch the location itself (not promotion).
        async fetchLocation() {
            if (!this.locationUuid) return;
            try {
                this.loading = true;
                // Use the correct endpoint for locations
                axios.get(`/api/dashboard/locations/get/${this.locationUuid}`)
                    .then(res => {
                        this.populateForm(res.data);
                    })
                    .catch(err => {
                        console.error(err);
                        this.$toast.error('Failed to load Location.');
                    });

            } catch (error) {
                // Handle 404/NotFound specifically
                if (error.response && error.response.status === 404) {
                    this.$toast?.error('Location not found.');
                } else {
                    console.error('Error fetching location:', error);
                    this.$toast?.error('Failed to load location.');
                }
            } finally {
                this.loading = false;
            }
        },
        changeDescription(e) {
            this.$refs.locationtabs.recalculateHeight()
            this.description = e;
        },
        handleImageUpload(event) {
            const file = event.target.files[0];
            if (file) {
                this.image_upload = file;
                this.image_preview = URL.createObjectURL(file);
            }
            this.$refs.locationtabs.recalculateHeight()
        },
        handleLogoUpload(event) {
            const file = event.target.files[0];
            if (file) {
                this.logo_upload = file;
                this.logo_preview = URL.createObjectURL(file);
            }
            this.$refs.locationtabs.recalculateHeight()
        },
        onStepChange(newStep) {
            this.step = newStep;
        },
        async populateForm(location) {
            if (!location) return;
            this.location = location;
            this.locationUuid = location.uuid || '';
            this.resourceType = location.resource_type || '';

            console.log(location);
            this.name = location.name?.en || '';
            this.street = location.street || '';
            this.maps_url = location.maps_url || '';
            this.city = location.city || '';
            this.postcode = location.postcode || '';
            this.type = location.type || '';
            this.country = location.country || '';
            this.lat = location.lat || '';
            this.long = location.long || '';
            this.initialDescription = location.description?.en || '';
            this.default_name = location.default_name || '';
            this.image_upload = location.image_link || '';
            this.logo_upload = location.logo_link || '';
            this.slug = location.slug || '';

            this.initialOpeningHours = location.opening_hours || [];
            this.initialSpecialOpeningHours = location.special_opening_hours || [];
            this.hasOpeningHours = !!location.has_opening_hours;

            this.type = location.type || '';
            this.priceIndication = location.priceIndication || 0;

            this.$refs.locationtabs.recalculateHeight();
        },
        changedLocation(e) {
            this.street = e.street;
            this.city = e.city;
            this.postcode = e.postcode;
            this.country = e.country;
            this.lat = e.lat;
            this.long = e.long;
            this.$refs.locationtabs.recalculateHeight()
        },
        async saveLocation() {
            try {
                this.loading = true; // Visual feedback


                const formData = new FormData();
                const f = (k, v) => formData.append(k, v ?? '');

                // Basic Info
                f('uuid', this.locationUuid);
                f('name', this.name);
                f('default_name', this.default_name);
                f('description', this.description);

                // Address & SEO
                f('street', this.street);
                f('city', this.city);
                f('postcode', this.postcode);
                f('country', this.country);
                f('maps_url', this.maps_url);

                // Coordinates (ensuring they are numbers/strings)
                f('lat', this.lat);
                f('long', this.long);
                f('type', this.type);
                f('priceIndication', this.priceIndication);

                f('opening_hours', JSON.stringify(this.openingHours));
                f('special_opening_hours', JSON.stringify(this.specialOpeningHours));
                f('hasOpeningHours', this.hasOpeningHours);

                // Settings
                f('tos', this.tos ? 1 : 0);
                f('step', this.step);

                // File Uploads - Handling two different inputs
                if (this.$refs.logo_upload?.files?.length) {
                    formData.append('logo_upload', this.$refs.logo_upload.files[0]);
                }
                if (this.$refs.image_upload?.files?.length) {
                    formData.append('image_upload', this.$refs.image_upload.files[0]);
                }

                const response = await axios.post('/api/dashboard/locations/save', formData, {
                    withCredentials: true
                });

                this.$toast?.success('Location saved succesfully');
                this.showEnding = true;
                window.scrollTo({ top: 0, behavior: 'smooth' })
            } catch (e) {
                console.log(e);
                var message = e.response?.data?.message 
                    || e.response?.data?.error 
                    || e.message;
                this.$toast?.error(message);
            }
        }
    },
    watch: {
        itemId: 'fetchResourceItem',
        resourceType: 'fetchResourceItem',
        resource(val) {
            this.default_image = val?.image_link || null;
        }
    },
    mounted() {
        const query = this.$route.query || {};
        // pre-assign resourceType, itemId if passed in query
        if (query.type) this.resourceType = query.type;
        if (query.id) this.itemId = query.id;

        // If editing
        if (this.$route.params.uuid) {
            this.locationUuid = this.$route.params.uuid;
            this.fetchPermission();
            this.fetchLocation();
        }
    }
}
</script>